// AI-CONTEXT: See docs/features/platform-revenue-churn-trend.md
import { useState } from 'react'
import {
    ResponsiveContainer,
    LineChart,
    Line,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    CartesianGrid,
} from 'recharts'
import { CATEGORICAL } from '../../helpers/chartPalette'
import formatMonthLabel from '../../helpers/formatMonthLabel'
import { sectorSeriesLabel, NO_SECTOR, UNATTRIBUTED } from '../../helpers/dashboard/buildSectorSeriesRows'

// Hex en geen var(): een CSS-variabele werkt niet in een SVG-presentatie-
// attribuut en valt dan stil terug op zwart.
// AI-CONTEXT: See docs/patterns.md#ui-color-tokens
const RESTGROEP_KLEUR = { [NO_SECTOR]: '#b1bdc4', [UNATTRIBUTED]: '#8c96a1' }

/**
 * @param {string} slug
 * @param {number} index - positie onder de echte sectoren
 * @returns {string}
 */
const kleurVoor = (slug, index) => RESTGROEP_KLEUR[slug] || CATEGORICAL[index % CATEGORICAL.length]

/**
 * Verloop over de maanden, als totaal of uitgesplitst per sector. Gedeeld door
 * de omzet- en de churnpagina.
 *
 * Per sector altijd gestapelde staven: de delen tellen op tot het totaal, en
 * een stapel laat dat zien. Het totaal is een lijn (omzet, een stand) of een
 * staaf (churn, een aantal per maand).
 *
 * De hoogte staat in px in de CSS en niet op '100%': die verwijst naar de
 * ouder, en in een flex-blok lost dat op naar 0 en tekent recharts niets.
 * AI-CONTEXT: See docs/pitfalls.md#ui-chart-renders-blank-after-move
 *
 * @param {object} props
 * @param {Array<object>} props.rows - uit buildSectorSeriesRows
 * @param {Array<string>} props.sectors - uit buildSectorSeriesRows
 * @param {string|null} props.sectorsSince - eerste maand met een uitsplitsing
 * @param {'line'|'bar'} props.totalKind - hoe het totaal getekend wordt
 * @param {string} props.totalLabel - naam van het totaal in tooltip en legenda
 * @param {(value: number) => string} props.formatValue - voor de as en de tooltip
 */
const SectorTrendChart = ({ rows, sectors, sectorsSince, totalKind, totalLabel, formatValue }) => {
    const [mode, setMode] = useState(/** @type {'total' | 'sector'} */ ('total'))

    const echteSectoren = sectors.filter((slug) => !RESTGROEP_KLEUR[slug])
    const eersteMaand = rows.length ? rows[0].period : null

    const tooltip = (
        <Tooltip
            formatter={(value, name) => [value === null || value === undefined ? '—' : formatValue(value), name]}
            labelFormatter={(label) => label}
        />
    )
    const assen = [
        <CartesianGrid key='grid' strokeDasharray='3 3' vertical={false} />,
        <XAxis key='x' dataKey='label' axisLine={false} tickLine={false} />,
        <YAxis key='y' axisLine={false} tickLine={false} tickFormatter={formatValue} width={90} />,
    ]

    return (
        <div className='sector-trend-chart'>
            <div className='sector-trend-chart__modes'>
                <button
                    type='button'
                    className={mode === 'total' ? 'is-active' : ''}
                    aria-pressed={mode === 'total'}
                    onClick={() => setMode('total')}
                >
                    Totaal
                </button>
                <button
                    type='button'
                    className={mode === 'sector' ? 'is-active' : ''}
                    aria-pressed={mode === 'sector'}
                    onClick={() => setMode('sector')}
                >
                    Per sector
                </button>
            </div>

            {mode === 'sector' && !sectors.length && (
                <p className='sector-trend-chart__note'>
                    Er is nog geen uitsplitsing per sector. Die komt er na de volgende nachtelijke berekening bij.
                </p>
            )}

            {/* Wat er vóór de uitsplitsing lag is een gat, geen nul — en dat
                hoort de lezer te weten, anders leest het als 'toen nog niets'. */}
            {mode === 'sector' && sectorsSince && eersteMaand && sectorsSince > eersteMaand && (
                <p className='sector-trend-chart__note'>
                    {`Per sector bijgehouden sinds ${formatMonthLabel(sectorsSince)}.`}
                </p>
            )}

            {(mode === 'total' || sectors.length > 0) && (
                <div className='sector-trend-chart__plot'>
                    <ResponsiveContainer width='100%' height='100%'>
                        {mode === 'total' && totalKind === 'line' ? (
                            <LineChart data={rows} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
                                {assen}
                                {tooltip}
                                <Line type='monotone' dataKey='total' name={totalLabel} stroke={CATEGORICAL[0]} strokeWidth={2} />
                            </LineChart>
                        ) : (
                            <BarChart data={rows} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
                                {assen}
                                {tooltip}
                                {mode === 'total' && (
                                    <Bar dataKey='total' name={totalLabel} fill={CATEGORICAL[0]} />
                                )}
                                {mode === 'sector' && <Legend />}
                                {mode === 'sector' && sectors.map((slug) => (
                                    <Bar
                                        key={slug}
                                        dataKey={slug}
                                        name={sectorSeriesLabel(slug)}
                                        stackId='sector'
                                        fill={kleurVoor(slug, echteSectoren.indexOf(slug))}
                                    />
                                ))}
                            </BarChart>
                        )}
                    </ResponsiveContainer>
                </div>
            )}
        </div>
    )
}

export default SectorTrendChart
